import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import AppCard from '../../../components/cards/AppCard';
import {
  createRowActionConfig,
  deleteRowActionConfig,
  getAllRoles,
  getModules,
  getRowActionConfigs,
  seedRowActionConfigs,
  updateRowActionConfig,
} from '../../../services/adminApi';

const { Text } = Typography;

const ICON_OPTIONS = [
  { value: 'delete',      label: 'delete (trash)' },
  { value: 'edit',        label: 'edit (pencil)' },
  { value: 'mail',        label: 'mail (envelope)' },
  { value: 'shareAlt',    label: 'shareAlt (share)' },
  { value: 'swap',        label: 'swap (arrows)' },
  { value: 'thunderbolt', label: 'thunderbolt (lightning)' },
  { value: 'userAdd',     label: 'userAdd (add user)' },
  { value: 'userSwitch',  label: 'userSwitch (switch user)' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'navigate', label: 'Navigate (open page)' },
  { value: 'modal',    label: 'Modal (open popup)' },
  { value: 'api',      label: 'API (call endpoint)' },
  { value: 'copy',     label: 'Copy (clipboard)' },
];

const API_METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE'];

const EMPTY_ACTION = {
  key: '', label: '', icon: '', actionType: 'navigate',
  routeTemplate: '', apiMethod: 'POST', apiPath: '',
  permission: '', permissionKey: '', danger: false, showInList: true,
};

function newKey() {
  return `action_${Date.now()}`;
}

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeModuleOption(module) {
  if (typeof module === 'string') {
    return { value: module, label: moduleTitle(module) };
  }
  const value = (
    module?.module ?? module?.key ?? module?.moduleKey ?? module?.value ??
    module?.apiModule ?? module?.name ?? module?.modulename ?? module?.menuName
  );
  if (!value) return null;
  return {
    value,
    label: (
      module?.label ?? module?.moduleLabel ?? module?.moduleName ??
      module?.name ?? module?.modulename ?? module?.menuName ?? moduleTitle(value)
    ),
  };
}

function normalizeModuleOptions(modules) {
  const seen = new Set();
  return (Array.isArray(modules) ? modules : [])
    .map(normalizeModuleOption)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.value)) return false;
      seen.add(module.value);
      return true;
    });
}

/* ── Action Drawer ─────────────────────────────────────────────────────────── */
function ActionDrawer({ open, initial, onClose, onSave }) {
  const [form] = Form.useForm();
  const [actionType, setActionType] = useState('navigate');

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initial ?? EMPTY_ACTION);
      setActionType(initial?.actionType ?? 'navigate');
    }
  }, [open, initial, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    onSave(values);
  };

  return (
    <Drawer
      title={initial?.key ? 'Edit Action' : 'Add Action'}
      placement="right"
      width={480}
      open={open}
      onClose={onClose}
      extra={
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
          Save Action
        </Button>
      }
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_ACTION}>
        <Form.Item name="key" label="Key" rules={[{ required: true, message: 'Key is required' }]}>
          <Input placeholder="e.g. sourceCandidates" />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
          <Input placeholder="e.g. Source Candidates" />
        </Form.Item>
        <Form.Item name="icon" label="Icon" rules={[{ required: true, message: 'Icon is required' }]}>
          <Select options={ICON_OPTIONS} placeholder="Select icon" />
        </Form.Item>
        <Form.Item name="actionType" label="Action Type" rules={[{ required: true }]}>
          <Select
            options={ACTION_TYPE_OPTIONS}
            onChange={(v) => setActionType(v)}
          />
        </Form.Item>

        {actionType === 'navigate' && (
          <Form.Item name="routeTemplate" label="Route Template">
            <Input placeholder="e.g. /app/jobs/edit/:_id" />
          </Form.Item>
        )}

        {actionType === 'api' && (
          <>
            <Form.Item name="apiMethod" label="API Method">
              <Select options={API_METHOD_OPTIONS.map((m) => ({ value: m, label: m }))} />
            </Form.Item>
            <Form.Item name="apiPath" label="API Path">
              <Input placeholder="e.g. /jobs/:_id" />
            </Form.Item>
          </>
        )}

        <Form.Item name="permission" label="Role Permission (optional)">
          <Input placeholder="e.g. job.edit" />
        </Form.Item>
        <Form.Item name="permissionKey" label="Permission Key (row-level gate)" tooltip="Maps to record.actionPermission.<key>. Leave blank to always show.">
          <Input placeholder="e.g. edit, sourceCandidates, sharePermission" />
        </Form.Item>

        <Space size={32}>
          <Form.Item name="danger" label="Danger" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInList" label="Show in List" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
}

/* ── Main Section ──────────────────────────────────────────────────────────── */
export default function RowActionsSection() {
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState(0);

  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  /* Load modules + roles once */
  useEffect(() => {
    setModulesLoading(true);
    Promise.all([getModules(), getAllRoles()]).then(([mods, { roles: r }]) => {
      const moduleOptions = normalizeModuleOptions(mods);
      setModules(moduleOptions);
      setSelectedModule((current) => (
        moduleOptions.some((module) => module.value === current) ? current : moduleOptions[0]?.value ?? ''
      ));
      setRoles(r ?? []);
    }).catch(() => {
      setModules([]);
      setSelectedModule('');
    }).finally(() => {
      setModulesLoading(false);
    });
  }, []);

  /* Load configs when module changes */
  const loadConfigs = useCallback(async () => {
    if (!selectedModule) {
      setConfigs([]);
      setActiveConfig(null);
      setActions([]);
      setDirty(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getRowActionConfigs(selectedModule);
      setConfigs(data);
      const match = data.find((c) => c.roleId === selectedRoleId);
      setActiveConfig(match ?? null);
      setActions(match ? (match.rowActions ?? []) : []);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedModule, selectedRoleId]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  /* When role selection changes, find matching config from already-loaded configs */
  useEffect(() => {
    const match = configs.find((c) => c.roleId === selectedRoleId);
    setActiveConfig(match ?? null);
    setActions(match ? (match.rowActions ?? []) : []);
    setDirty(false);
  }, [selectedRoleId, configs]);

  /* Save */
  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeConfig) {
        await updateRowActionConfig(activeConfig.id ?? activeConfig._id, actions);
        message.success('Config saved');
      } else {
        const created = await createRowActionConfig({ module: selectedModule, roleId: selectedRoleId, rowActions: actions });
        const newDoc = created?.data ?? created;
        setActiveConfig(newDoc);
        setConfigs((prev) => [...prev, newDoc]);
        message.success('Config created');
      }
      setDirty(false);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* Seed defaults */
  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await seedRowActionConfigs();
      const count = res?.data?.seeded ?? res?.seeded ?? 0;
      message.success(`Seeded ${count} new config(s)`);
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  /* Delete config */
  const handleDeleteConfig = async () => {
    if (!activeConfig) return;
    try {
      await deleteRowActionConfig(activeConfig.id ?? activeConfig._id);
      message.success('Config deleted');
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
    }
  };

  /* Action row operations */
  const openAddDrawer = () => {
    setEditingAction(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (action) => {
    setEditingAction(action);
    setDrawerOpen(true);
  };

  const handleSaveAction = (values) => {
    if (editingAction) {
      setActions((prev) => prev.map((a) => (a.key === editingAction.key ? { ...a, ...values } : a)));
    } else {
      setActions((prev) => [...prev, { ...EMPTY_ACTION, ...values, _localKey: newKey() }]);
    }
    setDirty(true);
    setDrawerOpen(false);
  };

  const handleDeleteAction = (actionKey) => {
    setActions((prev) => prev.filter((a) => a.key !== actionKey));
    setDirty(true);
  };

  const moveAction = (index, dir) => {
    setActions((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const roleOptions = [
    { value: 0, label: 'All Roles (default)' },
    ...roles.map((r) => ({
      value: r.roleId ?? r.role_id ?? r.id,
      label: r.roleName ?? r.role_name ?? r.name,
    })).filter((r) => r.value),
  ];

  const columns = [
    { title: 'Order', key: 'order', width: 80,
      render: (_, __, i) => (
        <Space>
          <Button size="small" type="text" disabled={i === 0} onClick={() => moveAction(i, -1)}>↑</Button>
          <Button size="small" type="text" disabled={i === actions.length - 1} onClick={() => moveAction(i, 1)}>↓</Button>
        </Space>
      ),
    },
    { title: 'Key',   dataIndex: 'key',   key: 'key',   render: (v) => <Text code>{v}</Text> },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Icon',  dataIndex: 'icon',  key: 'icon',  render: (v) => v ? <Tag>{v}</Tag> : '—' },
    { title: 'Type',  dataIndex: 'actionType', key: 'actionType', render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Permission Key', dataIndex: 'permissionKey', key: 'permissionKey',
      render: (v) => v ? <Text type="secondary" code>{v}</Text> : <Text type="secondary">—</Text>,
    },
    { title: 'Danger',      dataIndex: 'danger',      key: 'danger',      render: (v) => v ? <Tag color="red">Yes</Tag> : '—' },
    { title: 'Show in List', dataIndex: 'showInList', key: 'showInList',  render: (v) => v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag> },
    { title: '', key: 'actions', width: 80, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
          </Tooltip>
          <Popconfirm title="Remove this action?" onConfirm={() => handleDeleteAction(record.key)} okText="Remove" okType="danger">
            <Tooltip title="Remove">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header card */}
      <AppCard icon={<ToolOutlined />} title="Row Action Config">
        <div className="admin-accent-bar" />
        <Typography.Title level={5}>Row Actions</Typography.Title>
        <Text type="secondary">
          Configure which action buttons appear in each module&apos;s list view, and control
          per-row visibility via permission keys. Configs are per module + role combination.
        </Text>
      </AppCard>

      {/* Selector + controls */}
      <AppCard
        icon={<ToolOutlined />}
        title="Select Config"
        extra={
          <Space wrap>
            <Tooltip title="Seed missing default configs for all tenants (existing configs are not overwritten)">
              <Button
                icon={<ReloadOutlined />}
                loading={seeding}
                onClick={handleSeed}
              >
                Seed Defaults
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Space wrap size={12}>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Module</Text>
            <Select
              style={{ width: 180 }}
              value={selectedModule}
              loading={modulesLoading}
              placeholder="Select module"
              onChange={(v) => setSelectedModule(v)}
              options={modules}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Role</Text>
            <Select
              style={{ width: 220 }}
              value={selectedRoleId}
              onChange={(v) => setSelectedRoleId(v)}
              options={roleOptions}
            />
          </div>
          <div style={{ paddingTop: 20 }}>
            <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
              Reload
            </Button>
          </div>
        </Space>
      </AppCard>

      {/* Error */}
      {error && <Alert type="error" message={error} showIcon />}

      {/* Actions table */}
      <AppCard
        icon={<ToolOutlined />}
        title={
          <Space>
            <span>Actions</span>
            {activeConfig
              ? <Tag color="green">Config loaded (roleId: {selectedRoleId})</Tag>
              : <Tag color="orange">No config — will create on Save</Tag>
            }
          </Space>
        }
        extra={
          <Space>
            {activeConfig && (
              <Popconfirm title="Delete this entire config?" onConfirm={handleDeleteConfig} okText="Delete" okType="danger">
                <Button danger size="small" icon={<DeleteOutlined />}>Delete Config</Button>
              </Popconfirm>
            )}
            <Button icon={<PlusOutlined />} disabled={!selectedModule} onClick={openAddDrawer}>Add Action</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty || !selectedModule}
              onClick={handleSave}
            >
              Save Config
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Table
            dataSource={actions.map((a, i) => ({ ...a, _rowKey: a.key || a._localKey || i }))}
            rowKey="_rowKey"
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: 900 }}
            locale={{ emptyText: selectedModule ? 'No actions configured. Click "Add Action" to start.' : 'No modules found. Configure modules first.' }}
          />
        </Spin>
      </AppCard>

      <ActionDrawer
        open={drawerOpen}
        initial={editingAction}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveAction}
      />
    </div>
  );
}
